import React, { useState } from "react";
import { ALT_ASSETS, RISK_OPTIONS, LIQUIDITY_OPTIONS } from "./data.js";

export default function ReconStage({ onComplete }) {
  const [index, setIndex] = useState(0);
  const [tags, setTags] = useState({});
  const [correctCount, setCorrectCount] = useState(0);
  const [flash, setFlash] = useState(null);

  const current = ALT_ASSETS[index];
  const done = index >= ALT_ASSETS.length;

  const chooseTag = (field, value) => {
    if (!current) return;
    const existing = tags[current.id] || {};
    const updated = { ...existing, [field]: value };
    setTags((prev) => ({ ...prev, [current.id]: updated }));

    if (updated.risk && updated.liquidity) {
      const bothCorrect = updated.risk === current.correctRisk && updated.liquidity === current.correctLiquidity;
      if (bothCorrect) setCorrectCount((c) => c + 1);
      setFlash(bothCorrect ? "correct" : "off");
      setTimeout(() => {
        setFlash(null);
        setIndex((i) => i + 1);
      }, 700);
    }
  };

  if (done) {
    return (
      <div className="animate-popIn text-center">
        <p className="label-pixel mb-2 text-lime">Recon Complete</p>
        <h2 className="pixel-heading mb-4 text-base text-mist">Frontier Mapped</h2>

        <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-mist/60">Classified Correctly</span>
            <span className="text-lime">
              {correctCount}/{ALT_ASSETS.length}
            </span>
          </div>
        </div>

        <p className="mx-auto mb-6 max-w-sm text-xs text-mist/60">
          Every one of these can build wealth — but none of them work like a simple index fund.
          Before any of it, the boring fundamentals have to already be in place.
        </p>

        <button onClick={() => onComplete({ correctCount })} className="btn-magenta">
          CHECK THE PREREQUISITES ▶
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>RECON</span>
        <span>
          {index}/{ALT_ASSETS.length} REVIEWED
        </span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Five ways to build wealth outside the stock market. Tag each by risk level and how easily
        you could actually turn it back into cash.
      </p>

      <div
        key={current.id}
        className={`mb-5 border-2 border-black bg-ink p-5 transition-colors ${
          flash === "correct" ? "border-lime shadow-glow-cyan" : flash === "off" ? "border-danger" : ""
        }`}
      >
        <p className="pixel-heading mb-2 text-sm text-mist">{current.title}</p>
        <p className="text-xs leading-relaxed text-mist/70">{current.description}</p>
        {flash && <p className="mt-3 text-[11px] text-mist/50">{current.note}</p>}
      </div>

      <div className="mb-4">
        <p className="label-pixel mb-2 text-mist/60">Risk Level</p>
        <div className="grid grid-cols-3 gap-2">
          {RISK_OPTIONS.map((opt) => {
            const selected = tags[current.id]?.risk === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => chooseTag("risk", opt.id)}
                className={`border-2 px-2 py-2 font-display text-[8px] uppercase transition-colors ${
                  selected ? "border-cyan bg-cyan text-ink" : "border-mist/30 text-mist/70 hover:border-cyan"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="label-pixel mb-2 text-mist/60">Liquidity</p>
        <div className="grid grid-cols-3 gap-2">
          {LIQUIDITY_OPTIONS.map((opt) => {
            const selected = tags[current.id]?.liquidity === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => chooseTag("liquidity", opt.id)}
                className={`border-2 px-2 py-2 font-display text-[8px] uppercase transition-colors ${
                  selected ? "border-magenta bg-magenta text-ink" : "border-mist/30 text-mist/70 hover:border-magenta"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
